import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '@/services/api';
import { usePageTitle } from '@/shared/usePageTitle';
import { buildIcs, downloadTextFile } from '@/shared/exports';
import { PageHeader } from '@/shared/PageHeader';
import { useAuth } from '@/shared/AuthContext';

type Status = 'Available' | 'OnCall' | 'NotAvailable';

const STATUS_CLASS: Record<Status, string> = {
  Available: 'status-available',
  OnCall: 'status-oncall',
  NotAvailable: 'status-notavail',
};
const STATUS_LABEL: Record<Status, string> = {
  Available: 'Disp.',
  OnCall: 'Reperibile',
  NotAvailable: 'Non disp.',
};

export function AvailabilityPage(): React.JSX.Element {
  usePageTitle('Disponibilita');
  const qc = useQueryClient();
  const { user } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [selectedStatus, setSelectedStatus] = useState<Status>('Available');

  const { data: current } = useQuery({
    queryKey: ['availability.current'],
    queryFn: async () => (await api.get('/availability/current')).data,
  });

  const { data: calendar } = useQuery({
    queryKey: ['availability.calendar', year, month],
    queryFn: async () => (await api.get('/availability/calendar', { params: { year, month } })).data,
  });

  const setDay = useMutation({
    mutationFn: async (date: string) => {
      await api.post('/availability/calendar', { date, status: selectedStatus });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['availability.calendar'] }),
  });

  // Stato corrente con 3 modalita': Available (override on), NotAvailable
  // (override off), FollowCalendar (decide il calendario per il giorno corrente).
  const setMode = useMutation({
    mutationFn: async (mode: 'Available' | 'NotAvailable' | 'FollowCalendar') => {
      await api.post('/availability/mode', { mode });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['availability.current'] }),
  });

  const toggleDnd = useMutation({
    mutationFn: async (isDoNotDisturb: boolean) => { await api.post('/availability/dnd', { isDoNotDisturb }); },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['availability.current'] }),
  });

  const daysInMonth = new Date(year, month, 0).getDate();
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const calendarMap: Record<string, Status> = {};
  (calendar ?? []).forEach((e: { date: string; status: Status }) => { calendarMap[e.date] = e.status; });

  function exportIcs() {
    if (!calendar?.length) return;
    const ics = buildIcs(calendar, user?.nickname ?? 'vvf');
    downloadTextFile(ics, `disponibilita_${year}_${String(month).padStart(2, '0')}.ics`, 'text/calendar');
  }

  return (
    <div className="page-container">
      <PageHeader title="📅 Disponibilità"
        actions={(
          <button onClick={exportIcs} className="btn-info"
            style={{ padding: '10px 20px', borderRadius: 8, fontWeight: 600, border: 'none', cursor: 'pointer' }}>
            📆 Export ICS
          </button>
        )} />

      <div className="card">
        <h3>Stato corrente</h3>
        <p style={{ color: '#666', marginBottom: 12, fontSize: 14 }}>
          <strong>Disponibile</strong> / <strong>Non disponibile</strong>: override esplicito, vince sul calendario.<br />
          <strong>Segui calendario</strong>: usa il calendario qui sotto per decidere giorno per giorno.
        </p>
        <div className="toolbar" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <MyCurrentStatusBadge current={current} />
          <ModeButton
            current={current}
            mode="Available"
            label="✓ Disponibile"
            className="btn-success"
            onClick={() => setMode.mutate('Available')}
          />
          <ModeButton
            current={current}
            mode="NotAvailable"
            label="✗ Non disponibile"
            className="btn-danger"
            onClick={() => setMode.mutate('NotAvailable')}
          />
          <ModeButton
            current={current}
            mode="FollowCalendar"
            label="📅 Segui calendario"
            className="btn-info"
            onClick={() => setMode.mutate('FollowCalendar')}
          />
          <MyDndToggle current={current} onToggle={(v) => toggleDnd.mutate(v)} />
        </div>
      </div>

      <div className="card">
        <h3>Vista distaccamento</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 6 }}>
          {(current ?? []).map((u: CurrentUser) => (
            <div key={u.userId} className={`user-row ${u.isDoNotDisturb ? 'dnd' : u.isAvailable ? 'available' : 'unavailable'}`}>
              <span><strong>{u.nickname}</strong></span>
              <span>{u.isDoNotDisturb ? '🔕' : u.isAvailable ? '✓' : '✗'}</span>
            </div>
          ))}
          {(current ?? []).length === 0 && <div className="empty-state">Nessun utente</div>}
        </div>
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <h3 style={{ margin: 0 }}>Calendario personale</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="btn-secondary" style={{ padding: '6px 10px' }} onClick={() => {
              const d = new Date(year, month - 2, 1);
              setYear(d.getFullYear()); setMonth(d.getMonth() + 1);
            }}>«</button>
            <strong style={{ minWidth: 80, textAlign: 'center' }}>{String(month).padStart(2, '0')}/{year}</strong>
            <button className="btn-secondary" style={{ padding: '6px 10px' }} onClick={() => {
              const d = new Date(year, month, 1);
              setYear(d.getFullYear()); setMonth(d.getMonth() + 1);
            }}>»</button>
          </div>
        </div>

        <div className="toolbar" style={{ marginTop: 16 }}>
          <span style={{ fontSize: 13, color: '#666', fontWeight: 600 }}>Imposta con click:</span>
          {(['Available', 'OnCall', 'NotAvailable'] as Status[]).map(s => (
            <button key={s}
              onClick={() => setSelectedStatus(s)}
              className={s === 'Available' ? 'btn-success' : s === 'OnCall' ? 'btn-warning' : 'btn-danger'}
              style={{
                padding: '6px 14px', fontSize: 13,
                outline: selectedStatus === s ? '3px solid #333' : 'none',
              }}>
              {STATUS_LABEL[s]}
            </button>
          ))}
        </div>

        <div className="calendar-grid">
          {days.map(d => {
            const iso = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
            const status = calendarMap[iso];
            return (
              <div key={d} className={`calendar-day ${status ? STATUS_CLASS[status] : ''}`}
                onClick={() => setDay.mutate(iso)}>
                {d}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

type AvailabilityMode = 'Available' | 'NotAvailable' | 'FollowCalendar';

interface CurrentUser {
  userId: string;
  nickname: string;
  isAvailable: boolean;
  isDoNotDisturb?: boolean;
  /** Override esplicito; assente sui client legacy. */
  mode?: AvailabilityMode;
}

function MyCurrentStatusBadge({ current }: { current: CurrentUser[] | undefined }): React.JSX.Element {
  const { user } = useAuth();
  const myId = user?.id ?? null;
  const me = (current ?? []).find(u => u.userId === myId);
  const isDnd = me?.isDoNotDisturb === true;
  const isAvail = !isDnd && me?.isAvailable === true;
  const mode = me?.mode;
  const baseLabel = isDnd ? 'Non disturbare' : isAvail ? 'Disponibile' : 'Non disponibile';
  // Suffisso per chiarire la fonte dello stato quando segue il calendario.
  const label = !isDnd && mode === 'FollowCalendar'
    ? `${baseLabel} (calendario)`
    : baseLabel;
  const bg = isDnd ? '#fff3cd' : isAvail ? '#d4edda' : '#f8d7da';
  const fg = isDnd ? '#856404' : isAvail ? '#155724' : '#721c24';
  const border = isDnd ? '#ffeeba' : isAvail ? '#c3e6cb' : '#f5c6cb';
  const dot = isDnd ? '#ffc107' : isAvail ? '#28a745' : '#dc3545';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '6px 12px', borderRadius: 18, fontWeight: 700, fontSize: 13,
      background: bg, color: fg, border: `1px solid ${border}`,
    }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: dot }} />
      Stato: <strong>{label}</strong>
    </span>
  );
}

/** Bottone per impostare la modalita'. Evidenziato se gia' attivo. */
function ModeButton({ current, mode, label, className, onClick }: {
  current: CurrentUser[] | undefined;
  mode: AvailabilityMode;
  label: string;
  className: string;
  onClick: () => void;
}): React.JSX.Element {
  const { user } = useAuth();
  const myId = user?.id ?? null;
  const me = (current ?? []).find(u => u.userId === myId);
  const isActive = me?.mode === mode;
  return (
    <button
      onClick={onClick}
      className={className}
      style={{ outline: isActive ? '3px solid #333' : 'none', fontWeight: isActive ? 700 : 500 }}
      title={isActive ? 'Modalita attiva' : 'Imposta modalita'}>
      {label}
    </button>
  );
}

function MyDndToggle({ current, onToggle }: { current: CurrentUser[] | undefined; onToggle: (v: boolean) => void }): React.JSX.Element {
  const { user } = useAuth();
  const myId = user?.id ?? null;
  const me = (current ?? []).find(u => u.userId === myId);
  const isDnd = me?.isDoNotDisturb === true;
  return (
    <button
      onClick={() => onToggle(!isDnd)}
      className={isDnd ? 'btn-warning' : 'btn-secondary'}
      title="Non disturbare: nessuna selettiva, nessun suono">
      {isDnd ? '🔕 DND attivo' : '🔕 Non disturbare'}
    </button>
  );
}
